import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { FuelConsumption, PentaDashboard } from '@volvogroup-internal/dashboard';
import { useTranslation } from 'react-i18next';

import Table from '@/components/Table';
import { getToken } from '@/config/MsalAuthConfig';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import useSessionStorage from '@/hooks/useLocalStorage';
import { getFuelConsumptionByMonthQueryOptions } from '@/service/vpc/queryOptions';
import { client } from '@/service/vpc/vpcService';
import useFuelConsumptionTable, { mapFuelConsumptionTableData } from '@/tables/useFuelConsumptionTable';
import { chassis } from '@/utils';

export const Route = createFileRoute('/_authenticated/_account/dashboard/fuel-consumption/')({
  component: FuelConsumptionPage,
});

function FuelConsumptionPage() {
  const { i18n, t } = useTranslation();
  const queryClient = useQueryClient();

  const { columns } = useFuelConsumptionTable();
  const { fleetVerifiedUnits } = useFleetAccount();
  const { value: filterYear } = useSessionStorage('vpckey-filter-year', new Date().getFullYear());
  const { value: filterMonth } = useSessionStorage('vpckey-filter-month', new Date().getMonth() + 1);

  const chassisInfoFromUnits = chassis.extractChassisIds(fleetVerifiedUnits || []);
  const { data, isLoading } = useQuery(
    getFuelConsumptionByMonthQueryOptions({
      chassisIds: chassisInfoFromUnits.join(','),
      year: filterYear,
      month: filterMonth.toString().padStart(2, '0'),
    })
  );

  const endCustomerUnitIds = fleetVerifiedUnits?.map((unit) => unit.endCustomerUnitId) || [];
  const tableData = mapFuelConsumptionTableData(data, fleetVerifiedUnits || []);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <PentaDashboard
          client={{
            httpClient: {
              instance: client,
              getToken: getToken,
            },
            queryClient,
          }}
          language={i18n.language}
          preFilterUnits={endCustomerUnitIds}
          showFilterMenu={true}
          showFilterBar={true}
        >
          <FuelConsumption type="monthly" />
        </PentaDashboard>
      </div>

      <Table
        headerTitle={t(($) => $.pages.account.dashboard.fuel_consumption.details_title)}
        isLoading={isLoading}
        data={tableData}
        columns={columns}
        showSearch={true}
        showPagination={false}
        enableExpanding={true}
        subRowsKey="subRows"
      />
    </div>
  );
}
